import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { verifyEmailToken } from "../api/auth";
import { AuthShell } from "../components/AuthShell";

/**
 * Confirm an email address from the link in the verification mail.
 *
 * The link arrives as `/verify-email/<token>` on whichever frontend the
 * registration came from — the API reads the request's `Origin` against the
 * `PARTNER_FRONTEND_URL` allowlist and mails the panel host for a partner, the
 * consumer site for everyone else.
 *
 * The token is read from the path and posted immediately, so there is no form.
 * It runs once per token: the ref guards against a double invocation under
 * React 18 StrictMode, which would send the same single-use token twice and
 * surface as a spurious "expired link".
 */
export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<"verifying" | "done" | "error">("verifying");
  const [message, setMessage] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    verifyEmailToken(token)
      .then(() => setStatus("done"))
      .catch((err: unknown) => {
        setMessage(err instanceof ApiError ? err.message : "This link could not be verified.");
        setStatus("error");
      });
  }, [token]);

  if (status === "verifying") {
    return (
      <AuthShell title="Verifying your email" lede="This only takes a moment.">
        <p className="text-sm text-ink-muted">Checking your link…</p>
      </AuthShell>
    );
  }

  if (status === "done") {
    return (
      <AuthShell title="Email confirmed" lede="Your address is verified.">
        <p className="text-sm leading-relaxed text-ink-muted">
          You can sign in with the password you chose during registration.
        </p>
        <Link
          to="/login"
          className="mt-5 inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-sm font-medium text-white"
        >
          Sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="This link did not work"
      footer={
        <Link to="/login" className="font-medium text-primary underline">
          Go to sign in
        </Link>
      }
    >
      <p className="text-sm leading-relaxed text-ink-muted">{message}</p>
      <p className="mt-3 text-sm leading-relaxed text-ink-muted">
        Verification links work once and expire. If yours has already been used or has aged out,
        register again to get a fresh one.
      </p>
    </AuthShell>
  );
}